import React, { useState, useCallback } from 'react';
import './ValidationError.less';
import { ValidationSummary } from 'utils/ValidationUtils';
import { ValidationList } from './ValidationList';
import { Fade } from '@material-ui/core';
import { CALLOUT_CARET_SIZE, CalloutCaret } from 'components/layout/CalloutCaret';
import { Popper } from 'react-popper';
import { createPortal } from 'react-dom';
import { getMaxZIndex } from 'utils/DomUtils';
import { useScrollSafe } from 'contexts/ScrollContext';
import { Placement } from 'popper.js';

export type ValidationRole = 'tooltip' | 'list' | 'alert';

export interface IValidationErrorProps extends ValidationSummary {
  id?: string;
  className?: string;
  sourceRef?: HTMLElement;
  /**
   * Default behavior is 'tooltip'. Specify other to change rendering behavior.
   */
  role?: ValidationRole;
  isOpen?: boolean;
  showTooltipAtCenter?: boolean;
  validationTextClassName?: string;
  hideErrorIcon?: boolean;
  placement?: Placement;
  solutions?: React.ReactNode;
  solutionsTitle?: string;
  /** if it has solutions, and is therefore interactive, might want to close it on mouse-out */
  onMouseLeave?: React.MouseEventHandler;
}

/**
 * Validation Error display component. These should be show on hover.
 *
 * This implementation relies on CSS transitions.
 * if we want something more sophisticated than CSS transitions for these, we should consider
 * https://github.com/react-spring/react-spring to handle transitions across the UI.
 *
 * We could split the tool-tip part of this out...
 *
 * Copyright (c) 2020 Decisions All Rights Reserved
 */

export function ValidationError({
  id,
  reasons,
  levels,
  worstLevel,
  role,
  className,
  sourceRef,
  isOpen,
  showTooltipAtCenter,
  validationTextClassName,
  placement,
  hideErrorIcon,
  solutions,
  solutionsTitle = 'Suggested Solutions:',
  onMouseLeave,
}: IValidationErrorProps) {
  const hasReasons = reasons && reasons.length;
  const [zIndex, setZIndex] = useState<number>(0);
  const { scrollParent } = useScrollSafe();

  const getZIndex = useCallback(() => {
    setZIndex(hasReasons ? getMaxZIndex() : 0);
  }, [hasReasons]);

  if (!hasReasons) {
    return null;
  }

  return role === 'list' ? (
    <ValidationList
      id={id}
      className={className}
      role={role}
      reasons={reasons}
      levels={levels}
      worstLevel={worstLevel}
    />
  ) : (
    createPortal(
      <Popper
        placement={placement ?? 'bottom-start'}
        referenceElement={sourceRef}
        eventsEnabled={true}
        modifiers={{
          offset: {
            // "length, width" relative to the "placement" orientation https://popper.js.org/docs/v1/#modifiers..offset
            offset: `0, ${CALLOUT_CARET_SIZE * 0.8}px`,
          },
          arrow: !showTooltipAtCenter
            ? {
                /** accommodate popper arrow being near left side of input: */
                fn: data => {
                  data.offsets.arrow = {
                    ...data.offsets.arrow,
                    left: data.offsets.reference.left - data.offsets.popper.left + 16,
                  };
                  return data;
                },
              }
            : undefined, //No need to perform calculation if we want to show arrow in center
          preventOverflow: {
            boundariesElement: scrollParent || 'window',
          },
        }}
      >
        {({ ref, style, arrowProps, outOfBoundaries, placement, scheduleUpdate }) => (
          <Fade onEnter={getZIndex} timeout={200} in={isOpen && !outOfBoundaries}>
            <ValidationList
              ref={ref}
              id={id}
              className={className}
              role={'alert'} //https://www.w3.org/WAI/GL/wiki/Using_ARIA_role_of_alert_for_Error_Feedback_in_Forms
              reasons={reasons}
              levels={levels}
              worstLevel={worstLevel}
              style={{ ...style, zIndex }}
              hideErrorIcon={hideErrorIcon}
              validationTextClassName={validationTextClassName}
              scheduleUpdate={scheduleUpdate}
              isOpen={isOpen}
              hasSolutions={!!solutions}
              onMouseLeave={onMouseLeave}
            >
              <CalloutCaret {...arrowProps} placement={placement} />
              {solutions && (
                <>
                  <h5 className="dpc-validation-error__solution">{solutionsTitle}</h5>
                  {solutions}
                </>
              )}
            </ValidationList>
          </Fade>
        )}
      </Popper>,
      document.body
    )
  );
}
